import type { ComponentType, SVGProps } from 'react';
import type { SidebarLayout } from '../../shared/prefs';
import {
  CallSheetsIcon,
  ClientsIcon,
  CrewIcon,
  DashboardsIcon,
  DocumentsIcon,
  DrivesIcon,
  FinanceIcon,
  LeadsIcon,
  MarketingIcon,
  MediaIcon,
  MeetingsIcon,
  OutreachIcon,
  ProjectsIcon,
  ProposalsIcon,
  ReviewsIcon,
  ScriptsIcon,
  SocialIcon,
  StatsIcon,
  TasksIcon,
} from '../components/icons';

export interface SidebarNavItem {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  badge?: 'leads' | 'projects' | 'tasks' | 'finance' | 'outreach' | 'drives';
  alert?: boolean;
  tool?: boolean;
}

// Metadata lives here once; saved preferences only carry stable route ids.
export const SIDEBAR_NAV_ITEMS: Record<string, SidebarNavItem> = {
  '/marketing': { to: '/marketing', label: 'Marketing', icon: MarketingIcon },
  '/leads': { to: '/leads', label: 'Leads', icon: LeadsIcon, badge: 'leads', alert: true },
  // Cold leads pushed by the scraper — its own pipeline, not the Leads table.
  '/outreach': {
    to: '/outreach',
    label: 'Outreach',
    icon: OutreachIcon,
    badge: 'outreach',
    alert: true,
  },
  '/projects': {
    to: '/projects',
    label: 'Projects',
    icon: ProjectsIcon,
    badge: 'projects',
    alert: false,
  },
  '/tasks': { to: '/tasks', label: 'Tasks', icon: TasksIcon, badge: 'tasks', alert: true },
  '/meetings': { to: '/meetings', label: 'Meetings', icon: MeetingsIcon },
  '/clients': { to: '/clients', label: 'Clients', icon: ClientsIcon },
  '/crew': { to: '/crew', label: 'Crew', icon: CrewIcon },
  '/drives': { to: '/drives', label: 'Drives', icon: DrivesIcon, badge: 'drives', alert: true },
  '/finance': {
    to: '/finance',
    label: 'Finance',
    icon: FinanceIcon,
    badge: 'finance',
    alert: true,
  },
  '/stats': { to: '/stats', label: 'Stats', icon: StatsIcon },
  // Native page, not an embedded tool — no `tool: true` ↗ marker.
  '/social': { to: '/social', label: 'Social', icon: SocialIcon },
  // Native library of published scripts and call sheets — also no ↗.
  '/systems': { to: '/systems', label: 'Systems', icon: DocumentsIcon },
  '/documents': { to: '/documents', label: 'Documents', icon: DocumentsIcon },
  '/dashboards': { to: '/dashboards', label: 'Dashboards', icon: DashboardsIcon },
  '/tools/scripts': { to: '/tools/scripts', label: 'Scripts', icon: ScriptsIcon, tool: true },
  '/tools/callsheets': {
    to: '/tools/callsheets',
    label: 'Call sheets',
    icon: CallSheetsIcon,
    tool: true,
  },
  '/tools/reviews': { to: '/tools/reviews', label: 'Reviews', icon: ReviewsIcon, tool: true },
  '/tools/proposals': {
    to: '/tools/proposals',
    label: 'Proposals',
    icon: ProposalsIcon,
    tool: true,
  },
  '/tools/media': { to: '/tools/media', label: 'Media', icon: MediaIcon, tool: true },
};

export const DEFAULT_SIDEBAR_LAYOUT: SidebarLayout = {
  work: ['/projects', '/tasks', '/meetings', '/clients', '/crew', '/drives'],
  growth: ['/leads', '/marketing', '/outreach'],
  money: ['/finance', '/stats'],
  tools: [
    '/social',
    '/documents',
    '/systems',
    '/tools/scripts',
    '/tools/callsheets',
    '/tools/reviews',
    '/tools/proposals',
    '/tools/media',
    '/dashboards',
  ],
};

export const SIDEBAR_GROUP_LABELS = {
  work: 'Work',
  growth: 'Growth',
  money: 'Money',
  tools: 'Tools',
} as const;
